/**
 * PracticeMeetingCard — handoff §3.3.
 *
 * Two-column <Widget>. Left column shows synced practice fields + AM-
 * editable settings (APU ID, reporting type, submission method). Right
 * column shows meeting metadata. Snapshot timestamp + ↻ Recompute live in
 * the Widget's `actions` slot.
 *
 * The "edit settings" affordance is the small Edit button at the bottom of
 * the left column; it opens a modal that PATCHes the clinic admin row.
 *
 * Recompute calls POST /v1/clinics/{cid}/recompute (synchronous, ~3-5s).
 * Spinner replaces the button label until the dashboard query refetches.
 */
import { useState } from "react";
import { Widget } from "@tensaw/design-system";
import type { ClinicDetail, MeetingDetail, SnapshotDetail } from "@/api/models";
import { useRecompute } from "@/hooks/useRecompute";
import { formatDate, formatDateTime, formatDuration } from "@/lib/format";
import { PracticeSettingsModal } from "./PracticeSettingsModal";
import { MeetingEditModal } from "./MeetingEditModal";
import { type PinnedContext, pinnedTooltip } from "./types";

export interface PracticeMeetingCardProps {
  clinicId: number;
  year: number;
  clinic: ClinicDetail;
  meeting: MeetingDetail | null;
  snapshot: SnapshotDetail;
  pinned: PinnedContext | null;
}

function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <div className="text-xs font-medium uppercase tracking-wider text-gray-500">{label}</div>
      <div className="text-sm text-gray-800">{children}</div>
    </>
  );
}

export function PracticeMeetingCard({
  clinicId,
  year,
  clinic,
  meeting,
  snapshot,
  pinned,
}: PracticeMeetingCardProps) {
  const recompute = useRecompute(clinicId);
  const [practiceModalOpen, setPracticeModalOpen] = useState(false);
  const [meetingModalOpen, setMeetingModalOpen] = useState(false);

  const onRecompute = () => {
    recompute.mutate({ year });
  };

  const heldDuration =
    meeting?.held_started_at && meeting?.held_ended_at
      ? formatDuration(meeting.held_started_at, meeting.held_ended_at)
      : null;

  return (
    <>
      <Widget
        title="Practice & meeting"
        subtitle={`Computed snapshot · ${formatDate(snapshot.snapshot_date)}`}
        actions={
          <button
            type="button"
            onClick={onRecompute}
            disabled={recompute.isPending || pinned !== null}
            title={pinned ? pinnedTooltip(pinned) : undefined}
            className="inline-flex items-center gap-1 rounded-button border border-border bg-white px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {recompute.isPending ? "Recomputing…" : "↻ Recompute"}
          </button>
        }
      >
        {pinned ? (
          <div className="-mx-4 -mt-2 mb-3 border-b border-sky-200 bg-sky-50 px-4 py-2 text-xs text-sky-800">
            <strong>Historical view</strong> · pinned to {pinned.meetingLabel} {pinned.year}
            {pinned.pinnedAsOf ? ` (${formatDateTime(pinned.pinnedAsOf)})` : ""}.
            {snapshot.snapshot_date === null ? (
              <span> No snapshot existed at meeting time — measure data shown as “—”.</span>
            ) : (
              <span> Showing the dashboard as it appeared at meeting time. Edits are disabled.</span>
            )}
          </div>
        ) : null}
        <div className="grid grid-cols-1 gap-x-8 gap-y-3 md:grid-cols-2">
          {/* Left column — practice */}
          <div className="grid grid-cols-[140px_1fr] gap-y-2">
            <MetaRow label="Practice">
              <strong>{clinic.name}</strong>
            </MetaRow>
            <MetaRow label="TIN">
              <span className="tnum">{clinic.tin}</span>
            </MetaRow>
            <MetaRow label="Group NPI">
              <span className="tnum">{clinic.group_npi ?? "—"}</span>
            </MetaRow>
            <MetaRow label="APU ID">
              <span className="tnum">{clinic.apu_id ?? "—"}</span>
            </MetaRow>
            <MetaRow label="Reporting Type">
              {clinic.reporting_type ?? "—"}
            </MetaRow>
            <MetaRow label="Submission Method">
              {clinic.submission_method ?? "—"}
            </MetaRow>
            <MetaRow label="Performance Period">
              <span className="tnum">
                {clinic.performance_year_start ?? "—"} → {clinic.performance_year_end ?? "—"}
              </span>
            </MetaRow>
            <div />
            <div>
              <button
                type="button"
                onClick={() => setPracticeModalOpen(true)}
                disabled={pinned !== null}
                title={pinned ? pinnedTooltip(pinned) : undefined}
                className="text-sm text-tw-teal-700 hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
              >
                Edit settings…
              </button>
            </div>
          </div>

          {/* Right column — meeting */}
          <div className="grid grid-cols-[160px_1fr] gap-y-2">
            <MetaRow label="Meeting">
              {meeting ? (
                <>
                  <strong>{meeting.label}</strong>
                  {meeting.scheduled_at && (
                    <span className="text-gray-500"> · scheduled {formatDateTime(meeting.scheduled_at)}</span>
                  )}
                </>
              ) : (
                <span className="italic text-gray-500">No meeting yet</span>
              )}
            </MetaRow>
            <MetaRow label="Held">
              {meeting?.held_started_at && meeting?.held_ended_at ? (
                <span className="tnum">
                  {formatDateTime(meeting.held_started_at)} – {formatDateTime(meeting.held_ended_at)}
                  {heldDuration ? <span className="text-gray-500"> · {heldDuration}</span> : null}
                </span>
              ) : (
                "—"
              )}
            </MetaRow>
            <MetaRow label="Account Manager">
              {meeting?.am_display_name ?? "—"}
            </MetaRow>
            <MetaRow label="Attendees (client)">
              {meeting?.client_attendees ?? "—"}
            </MetaRow>
            <MetaRow label="QPP Audit Ref">
              {meeting?.qpp_audit_ref ? (
                <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs">
                  {meeting.qpp_audit_ref}
                </span>
              ) : (
                "—"
              )}
            </MetaRow>
            <div />
            <div>
              {meeting ? (
                <button
                  type="button"
                  onClick={() => setMeetingModalOpen(true)}
                  disabled={pinned !== null}
                  title={pinned ? pinnedTooltip(pinned) : undefined}
                  className="text-sm text-tw-teal-700 hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
                >
                  Edit meeting…
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </Widget>

      {practiceModalOpen ? (
        <PracticeSettingsModal
          clinicId={clinicId}
          clinic={clinic}
          onClose={() => setPracticeModalOpen(false)}
        />
      ) : null}
      {meetingModalOpen && meeting ? (
        <MeetingEditModal
          clinicId={clinicId}
          meeting={meeting}
          onClose={() => setMeetingModalOpen(false)}
        />
      ) : null}
    </>
  );
}
